import { type FormEvent, useState } from "react";
import {
  type Comment,
  useAddComment,
  useComments,
  useDeleteComment,
  useMe,
  useToggleLike,
} from "../lib/api";
import { useAuthStore } from "../lib/auth-store";
import { Avatar } from "./Avatar";

function CommentItem({
  comment,
  canModerate,
  meUid,
  onLike,
  onDelete,
}: {
  comment: Comment;
  canModerate: boolean;
  meUid?: string;
  onLike: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const mine = comment.authorUid === meUid;
  return (
    <li className="rounded-lg border border-slate-800 px-3 py-2">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="flex items-center gap-1.5 text-cyan-300/80">
          <Avatar avatarUrl={comment.avatarUrl} nickname={comment.authorNickname} size={20} />
          {comment.authorNickname}
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onLike(comment.id)}
            className={
              comment.likedByMe
                ? "text-fuchsia-400"
                : "text-slate-500 transition hover:text-fuchsia-300"
            }
          >
            ❤ {comment.likeCount}
          </button>
          {(mine || canModerate) && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="text-slate-500 transition hover:text-fuchsia-400"
            >
              삭제
            </button>
          )}
        </div>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-200">{comment.body}</p>
    </li>
  );
}

export function CommentSection({ postId }: { postId: string }) {
  const fbUser = useAuthStore((s) => s.fbUser);
  const { data: me } = useMe(Boolean(fbUser));
  const { data: comments, isLoading } = useComments(postId);
  const add = useAddComment(postId);
  const del = useDeleteComment(postId);
  const like = useToggleLike(postId);
  const [text, setText] = useState("");

  const canModerate = me?.role === "admin" || me?.role === "sub_admin";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (text.trim().length === 0) return;
    add.mutate(text.trim());
    setText("");
  }

  return (
    <div className="mt-8 border-t border-slate-800 pt-5">
      <h2 className="text-sm font-semibold text-cyan-300/80">댓글 ({comments?.length ?? 0})</h2>

      <form className="mt-3 flex gap-2" onSubmit={onSubmit}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="댓글을 입력하세요"
          className="input-cyber flex-1 rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={add.isPending}
          className="btn-neon rounded-lg px-4 text-sm font-medium"
        >
          등록
        </button>
      </form>

      {isLoading ? (
        <p className="mt-4 text-center font-mono text-xs text-cyan-300/70">불러오는 중…</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {comments?.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              canModerate={Boolean(canModerate)}
              meUid={me?.uid}
              onLike={(id) => like.mutate({ targetType: "comment", targetId: id })}
              onDelete={(id) => del.mutate(id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
